import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Save, Settings, ExternalLink, Mail, Send, KeyRound } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    member_access_url: '#',
    brevo_api_key: '',
    email_from: 'ld.rakek@lovskazveza.si',
    email_from_name: 'LD Rakek',
    admin_notify_email: '',
    emails_enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data) {
        setSettings({
          member_access_url: response.data.member_access_url || '#',
          brevo_api_key: response.data.brevo_api_key || '',
          email_from: response.data.email_from || 'ld.rakek@lovskazveza.si',
          email_from_name: response.data.email_from_name || 'LD Rakek',
          admin_notify_email: response.data.admin_notify_email || '',
          emails_enabled: response.data.emails_enabled !== false,
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success('Nastavitve so bile shranjene');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Napaka pri shranjevanju');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    saveSettings();
  };

  const sendTest = async () => {
    if (!testEmail) {
      toast.error('Vnesite email za testno sporočilo');
      return;
    }
    setTesting(true);
    try {
      // First make sure settings are saved
      await axios.put(`${API}/settings`, settings);
      await axios.post(`${API}/camps/email/test`, { to: testEmail });
      toast.success('Testno sporočilo poslano!');
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Pošiljanje ni uspelo';
      toast.error(typeof detail === 'string' ? detail : 'Pošiljanje ni uspelo');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Settings className="w-6 h-6 text-emerald-400" />
        <h2 className="text-xl font-semibold text-white">Nastavitve strani</h2>
      </div>

      <Card className="bg-stone-800/50 border-stone-700/50 p-6">
        <form onSubmit={handleSave} className="space-y-6">
          <div className="space-y-2">
            <Label className="text-stone-300 flex items-center gap-2">
              <ExternalLink className="w-4 h-4" />
              URL za dostop članov
            </Label>
            <Input
              data-testid="member-access-url-input"
              value={settings.member_access_url}
              onChange={(e) => setSettings({ ...settings, member_access_url: e.target.value })}
              className="bg-stone-900/50 border-stone-700 text-white"
              placeholder="https://clanska-aplikacija.si"
            />
            <p className="text-xs text-stone-500">
              Vnesite URL zunanje aplikacije za dostop članov. Pustite &quot;#&quot; če še nimate URL-ja.
            </p>
          </div>

          {/* Email / Brevo settings */}
          <div className="border-t border-stone-700/50 pt-6 space-y-4">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-400" />
              <h3 className="text-lg font-semibold text-white">E-pošta (Brevo)</h3>
            </div>
            <p className="text-xs text-stone-500 -mt-2">
              Za pošiljanje potrditvenih e-poštnih sporočil ob prijavah na tabor.
              API ključ dobite v Brevo (https://app.brevo.com → SMTP &amp; API → API Keys).
            </p>

            <label className="flex items-center gap-3 text-stone-300">
              <Switch
                data-testid="emails-enabled-switch"
                checked={settings.emails_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, emails_enabled: v })}
              />
              Pošiljanje e-pošte omogočeno
            </label>

            <div className="space-y-2">
              <Label className="text-stone-300">Brevo API ključ</Label>
              <Input
                data-testid="brevo-api-key-input"
                type="password"
                value={settings.brevo_api_key}
                onChange={(e) => setSettings({ ...settings, brevo_api_key: e.target.value })}
                className="bg-stone-900/50 border-stone-700 text-white font-mono"
                placeholder="xkeysib-..."
                autoComplete="new-password"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-300">Email pošiljatelja (od)</Label>
                <Input
                  data-testid="email-from-input"
                  type="email"
                  value={settings.email_from}
                  onChange={(e) => setSettings({ ...settings, email_from: e.target.value })}
                  className="bg-stone-900/50 border-stone-700 text-white"
                  placeholder="ld.rakek@lovskazveza.si"
                />
                <p className="text-xs text-stone-500">Email mora biti potrjen v Brevo.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-stone-300">Ime pošiljatelja</Label>
                <Input
                  value={settings.email_from_name}
                  onChange={(e) => setSettings({ ...settings, email_from_name: e.target.value })}
                  className="bg-stone-900/50 border-stone-700 text-white"
                  placeholder="LD Rakek"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-stone-300">Email za obvestila skrbniku</Label>
              <Input
                data-testid="admin-notify-email-input"
                type="email"
                value={settings.admin_notify_email}
                onChange={(e) => setSettings({ ...settings, admin_notify_email: e.target.value })}
                className="bg-stone-900/50 border-stone-700 text-white"
                placeholder="ld.rakek@lovskazveza.si"
              />
              <p className="text-xs text-stone-500">
                Na ta naslov bo poslano obvestilo ob vsaki novi prijavi. Pustite prazno, da se uporabi email pošiljatelja.
              </p>
            </div>
          </div>

          <Button
            type="submit"
            disabled={saving}
            data-testid="save-settings-btn"
            className="bg-emerald-600 hover:bg-emerald-500"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Shranjujem...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                Shrani nastavitve
              </span>
            )}
          </Button>
        </form>
      </Card>

      {/* Test email */}
      <Card className="bg-stone-800/50 border-stone-700/50 p-6">
        <h3 className="text-lg font-semibold text-white mb-2">Testno e-poštno sporočilo</h3>
        <p className="text-stone-400 text-sm mb-4">
          Po shranjevanju nastavitev pošljite testno sporočilo, da preverite integracijo.
        </p>
        <div className="flex flex-col md:flex-row gap-3">
          <Input
            data-testid="test-email-input"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="vas.email@primer.si"
            className="bg-stone-900/50 border-stone-700 text-white flex-1"
          />
          <Button
            data-testid="send-test-email-btn"
            type="button"
            onClick={sendTest}
            disabled={testing || !settings.brevo_api_key}
            className="bg-emerald-600 hover:bg-emerald-500"
          >
            {testing ? 'Pošiljam...' : (<><Send className="w-4 h-4 mr-2" />Pošlji test</>)}
          </Button>
        </div>
      </Card>

      <ChangePasswordCard />
    </div>
  );
};

const ChangePasswordCard = () => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (next.length < 6) {
      toast.error('Novo geslo mora imeti vsaj 6 znakov');
      return;
    }
    if (next !== confirm) {
      toast.error('Novi gesli se ne ujemata');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: current,
        new_password: next,
      });
      toast.success('Geslo je bilo spremenjeno');
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Napaka pri spremembi gesla';
      toast.error(typeof detail === 'string' ? detail : 'Napaka');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submit();
  };

  return (
    <Card className="bg-stone-800/50 border-stone-700/50 p-6">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound className="w-5 h-5 text-emerald-400" />
        <h3 className="text-lg font-semibold text-white">Sprememba gesla</h3>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        <div className="space-y-2">
          <Label className="text-stone-300">Trenutno geslo</Label>
          <Input
            data-testid="current-password-input"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            autoComplete="current-password"
            className="bg-stone-900/50 border-stone-700 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-stone-300">Novo geslo (vsaj 6 znakov)</Label>
          <Input
            data-testid="new-password-input"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
            className="bg-stone-900/50 border-stone-700 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-stone-300">Ponovite novo geslo</Label>
          <Input
            data-testid="confirm-password-input"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            autoComplete="new-password"
            className="bg-stone-900/50 border-stone-700 text-white"
          />
        </div>
        <Button
          type="submit"
          data-testid="change-password-btn"
          disabled={submitting}
          className="bg-emerald-600 hover:bg-emerald-500"
        >
          {submitting ? 'Spreminjam...' : (<><KeyRound className="w-4 h-4 mr-2" />Spremeni geslo</>)}
        </Button>
      </form>
    </Card>
  );
};

export default AdminSettings;
